import { Injectable, TemplateRef } from '@angular/core';
import { Observable, filter, of, switchMap, concat, last, throwError } from 'rxjs';
import { OrderDataProvider, OrderViewModel } from '@lib/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { OrderHelper } from '../helper/order.helper';
import { PreOrder, PreOrderItem } from '../model/pre-order.model';

export enum OrderItemType {
  VOUCHER_ITEMS = 'voucherItems',
  GADGET_ITEMS = 'gadgetItems'
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class PreOrderService {
  private modalRef?: BsModalRef;
  preOrder: PreOrder = new PreOrder();

  constructor(
    protected orderItemsModel: OrderItemType,
    protected orderDataProvider: OrderDataProvider,
    protected orderHelper: OrderHelper,
    protected modalService: BsModalService
  ) {}

  showDetails<T>(
    content: string | TemplateRef<any> | (new (...args: any[]) => any),
    product: T,
    options: {
      propertyName: string;
      modalClass?: string;
      additionalConfig?: Partial<ModalOptions>;
    }
  ) {
    const initialState = {
      [options.propertyName]: product
    };

    const modalConfig = {
      initialState,
      class: options.modalClass || 'modal-md',
      ...options.additionalConfig
    };

    this.modalRef = this.modalService.show(content, modalConfig);
    return this.modalRef;
  }

  hideDetails() {
    this.modalRef.hide();
  }

  public cleanPreOrder() {
    this.preOrder.items = [];
  }

  updatePreOrderItem(item: PreOrderItem) {
    const existingItem = this.preOrder.items.find((vi) => vi.itemId === item.itemId);
    if (existingItem) {
      existingItem.quantity = item.quantity;
    } else {
      this.preOrder.items.push(item);
    }
  }

  verifyCompliance(order): boolean {
    const preOrderItems = this.preOrder?.items?.filter((item) => item.quantity > 0) || [];
    const orderItems = this.getOrderItems(order);

    if (preOrderItems.length !== orderItems.length) {
      return false;
    }

    return preOrderItems.every((preItem) => orderItems.some((orderItem) => orderItem.itemId === preItem.itemId && orderItem.quantity === preItem.quantity));
  }

  transferPreorderToOrder(cinemaId: string) {
    if (!cinemaId) {
      return throwError(() => new Error('Cinema ID is required'));
    }

    const itemsToDelete: string[] = [];
    const itemsToAdd: { itemId: string; quantity: number }[] = [];
    const itemsToUpdate: { id: string; itemId: string; quantity: number }[] = [];
    const preOrderItems = [...this.preOrder.items];

    return this.orderHelper.createIfNeeded(cinemaId).pipe(
      filter((order) => !!order),
      switchMap((order) => {
        const orderItems = [...this.getOrderItems(order)];
        preOrderItems.forEach((vi) => {
          const orderItem = orderItems?.find((oi) => oi.itemId === vi.itemId);
          if (orderItem) {
            if (vi.quantity === 0) {
              itemsToDelete.push(orderItem.id);
            } else if (orderItem.quantity !== vi.quantity) {
              itemsToUpdate.push({ id: orderItem.id, itemId: vi.itemId, quantity: vi.quantity });
            }
          } else {
            if (vi.quantity > 0) {
              itemsToAdd.push({ itemId: vi.itemId, quantity: vi.quantity });
            }
          }
        });

        const requests: Observable<OrderViewModel>[] = [];
        if (itemsToAdd.length) {
          console.log('items to add:', itemsToAdd);
          requests.push(this.orderDataProvider.addItems(this.orderItemsModel, cinemaId, order.id, itemsToAdd));
        }
        if (itemsToUpdate.length) {
          console.log('items to update:', itemsToUpdate);
          requests.push(this.orderDataProvider.updateItems(this.orderItemsModel, cinemaId, order.id, itemsToUpdate));
        }
        if (itemsToDelete.length) {
          console.log('items to delete:', itemsToDelete);
          requests.push(this.orderDataProvider.deleteItemsSequentially(cinemaId, order.id, itemsToDelete));
        }

        return requests.length ? concat(...requests).pipe(last()) : of(order);
      })
    );
  }

  // restoredPreOrderFromOrder(order: OrderViewModel) {
  //   if (order) {
  //     this.preOrder.items = this.getOrderItems(order).map((oi) => ({ id: oi.id, itemId: oi.itemId, quantity: oi.quantity }));
  //   }
  // }

  private getOrderItems(order: OrderViewModel) {
    if (!order) {
      return [];
    }

    return order[this.orderItemsModel] ?? [];
  }
}
